import { FC, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { DropDownButton, DropdownItem, IconName } from '@optimus-web/core';
import { LocalizedText, lt, showSuccessAlert, useDialog, useFeatureFlag } from '@mews/framework';
import {
    EnterpriseScopeCounterDiscriminator,
    getRepositoryQueryKey,
    useRepositoryMutation,
} from '@mews-commander/data-access';
import { canCurrentProfileManageCounters } from '@mews-commander/legacy-entities/src/selectors/session/scope';
import { useCommanderSelector, useCommanderThunkDispatch } from '@mews-commander/utils';
import { FeatureFlag, TextKey } from '@mews-commander/enums';
import { useCounter } from '../queries/useCounters';
import { deleteCounter, NonSystemCounterType, openEditCounterDialog, resetCounter } from '../actions';
import { useCanAddOrResetCounters } from '../utils';
import { DeleteCounterConfirmationDialog } from './CounterForm/DeleteCounterConfirmationDialog';
import { ResetCounterConfirmationDialog } from './CounterForm/ResetCounterConfirmationDialog';
import { CreditNoteCounterDefaultWarningDialog } from './creditNotes/CreditNoteCounterDefaultWarningDialog';
import { useEditCreditNoteCounterDialog } from './creditNotes/modal/useEditCreditNoteCounterDialog';
import { UpdateCreditNoteCounterDialog } from './creditNotes/UpdateCreditNoteCounterDialog';

interface CounterRowActionsProps {
    counterId: string;
}

export const CounterRowActions: FC<CounterRowActionsProps> = ({ counterId }) => {
    const dispatch = useCommanderThunkDispatch();
    const { data: counter } = useCounter(counterId);
    const [isUpdateCreditNoteDialogOpen, setIsUpdateCreditNoteDialogOpen] = useState(false);

    const newCreditNotDialogEnabled = useFeatureFlag(FeatureFlag.creditNoteCountersDialogRewrite);

    const [renderUpdateCreditNoteDialog, toggleUpdateCreditNoteDialog] = useEditCreditNoteCounterDialog({
        counterId,
    });
    const [warningDialogProps, toggleWarningDialog] = useDialog();

    const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);
    const [isResetConfirmationOpen, setIsResetConfirmationOpen] = useState(false);

    const { mutateAsync } = useRepositoryMutation('creditNoteBillCounters', 'update', {
        invalidateQueries: { counters: ['getAll'] },
    });

    const queryClient = useQueryClient();
    const canManageCounters = useCommanderSelector(canCurrentProfileManageCounters);
    const canAddOrResetCounters = useCanAddOrResetCounters();

    if (!counter || !canManageCounters) {
        return null;
    }

    const onEditButtonClick = () => {
        const discriminator = counter.data.discriminator as NonSystemCounterType;
        switch (discriminator) {
            case EnterpriseScopeCounterDiscriminator.CreditNoteBillCounter:
                if (newCreditNotDialogEnabled) {
                    toggleUpdateCreditNoteDialog();
                } else {
                    setIsUpdateCreditNoteDialogOpen(true);
                }
                break;
            default:
                dispatch(openEditCounterDialog(counterId, discriminator));
        }
    };

    const onDelete = async () => {
        await dispatch(deleteCounter(counterId));
        setIsDeleteConfirmationOpen(false);
        await queryClient.invalidateQueries({ queryKey: getRepositoryQueryKey('counters', 'getAll') });
    };

    const onReset = async () => {
        await dispatch(resetCounter(counterId));
        setIsResetConfirmationOpen(false);
        await queryClient.invalidateQueries({ queryKey: getRepositoryQueryKey('counters', 'getAll') });
    };

    const handleMakeDefault = async () => {
        await mutateAsync({
            counterId,
            isDefault: {
                value: true,
            },
        });
        showSuccessAlert({
            description: lt('CreditNoteCounterDefaultSuccessAlert'),
        });
    };

    return (
        <>
            <DropDownButton icon={IconName.More} data-counter-row-menu={counterId} variant="ghost">
                {counter.data.discriminator === EnterpriseScopeCounterDiscriminator.CreditNoteBillCounter ? (
                    <DropdownItem
                        label={<LocalizedText textKey={TextKey.MakeDefault} />}
                        onClick={toggleWarningDialog}
                        disabled={counter.isDefault}
                        data-counter-row-delete={counterId}
                    />
                ) : null}

                {counter.data.discriminator !== EnterpriseScopeCounterDiscriminator.FiscalCounter ? (
                    <DropdownItem
                        label={<LocalizedText textKey={TextKey.EditBillCounter} />}
                        onClick={onEditButtonClick}
                        data-counter-row-edit={counterId}
                    />
                ) : null}
                {canAddOrResetCounters ? (
                    <DropdownItem
                        label={<LocalizedText textKey={TextKey.ResetCounter} />}
                        onClick={() => setIsResetConfirmationOpen(true)}
                        data-counter-row-reset={counterId}
                    />
                ) : null}
                <DropdownItem
                    label={<LocalizedText textKey={TextKey.DeleteCounter} />}
                    onClick={() => setIsDeleteConfirmationOpen(true)}
                    disabled={
                        counter.isDefault &&
                        counter.data.discriminator !== EnterpriseScopeCounterDiscriminator.CreditNoteBillCounter
                    }
                    data-counter-row-delete={counterId}
                />
            </DropDownButton>
            {isDeleteConfirmationOpen ? (
                <DeleteCounterConfirmationDialog
                    isOpen={isDeleteConfirmationOpen}
                    onSubmit={onDelete}
                    onCancel={() => setIsDeleteConfirmationOpen(false)}
                />
            ) : null}
            {counter.data.discriminator === EnterpriseScopeCounterDiscriminator.CreditNoteBillCounter ? (
                <CreditNoteCounterDefaultWarningDialog
                    {...warningDialogProps}
                    onSubmit={handleMakeDefault}
                    applicableBillType={counter.data.value.applicableBillType}
                />
            ) : null}
            <UpdateCreditNoteCounterDialog
                id={counterId}
                active={isUpdateCreditNoteDialogOpen}
                handleClose={() => {
                    setIsUpdateCreditNoteDialogOpen(false);
                }}
            />

            {renderUpdateCreditNoteDialog()}

            {isResetConfirmationOpen ? (
                <ResetCounterConfirmationDialog
                    isOpen={isResetConfirmationOpen}
                    onSubmit={onReset}
                    onCancel={() => setIsResetConfirmationOpen(false)}
                />
            ) : null}
        </>
    );
};
